import React from 'react';
import ModulePanel from '../../ModulePanel';
import './Restreaming.css';

const View = ({
	list = [],
	setCurrentView,
	onDeleteRecord,
	onDoubleClick,
	onRegenerateConnectionCode,
	onTurnStatus,
	maxRestreaming,
	targetsCount,
	isConnected,
}) => {
	const customTargets = list.filter(t => t.id !== 'Wowza-CDN');
	const wowzaCDN = list.find(t => t.id === 'Wowza-CDN');

	return (
		<ModulePanel title={['Retransmisión Multicanal']}>
			{/* Header */}
			<div className="rs-header">
				<div className="rs-header-left">
					<div className="rs-counter">
						<span className="rs-counter-value">{targetsCount}</span>
						<span className="rs-counter-label">/ {maxRestreaming} destinos</span>
					</div>
					<StreamStatus isConnected={isConnected} />
				</div>
				
				<div className="rs-header-actions">
					<button
						disabled={targetsCount >= maxRestreaming}
						className="rs-btn rs-btn-add"
						onClick={() => setCurrentView('new')}
					>
						<i className="fas fa-plus" /> Agregar destino
					</button>
				</div>
			</div>

			{/* Quick Add Platforms */}
			<div className="rs-platforms">
				<span className="rs-platforms-label">Agregar rápido:</span>
				<button
					disabled={targetsCount >= maxRestreaming}
					className="rs-platform-btn rs-youtube"
					onClick={() => setCurrentView('new_youtube')}
				>
					<i className="fab fa-youtube" /> YouTube
				</button>
				<button
					disabled={targetsCount >= maxRestreaming}
					className="rs-platform-btn rs-facebook"
					onClick={() => setCurrentView('new_facebook')}
				>
					<i className="fab fa-facebook" /> Facebook
				</button>
				<button
					disabled={targetsCount >= maxRestreaming}
					className="rs-platform-btn rs-twitch"
					onClick={() => setCurrentView('new_twitch')}
				>
					<i className="fab fa-twitch" /> Twitch
				</button>
			</div>

			{/* Wowza CDN */}
			{wowzaCDN && (
				<div className="rs-cdn-bar">
					<i className="fas fa-cloud" />
					<span>Wowza CDN</span>
					<span className={`rs-cdn-status ${wowzaCDN.enable !== false ? 'active' : ''}`}>
						{wowzaCDN.enable !== false ? '● Activo' : '○ Inactivo'}
					</span>
					<button className="rs-cdn-refresh" onClick={onRegenerateConnectionCode}>
						<i className="fas fa-sync-alt" /> Renovar
					</button>
				</div>
			)}

			{/* Empty State */}
			{customTargets.length === 0 && (
				<div className="rs-empty">
					<i className="fas fa-satellite-dish" />
					<p>No hay destinos configurados</p>
					<span>Agrega destinos para transmitir a múltiples plataformas simultáneamente</span>
				</div>
			)}

			{/* Stream Targets Table */}
			{customTargets.length > 0 && (
				<table className="rs-table">
					<thead>
						<tr>
							<th style={{width: '40px'}}></th>
							<th>Destino</th>
							<th style={{width: '100px'}}>Habilitado</th>
							<th style={{width: '100px'}}>Acciones</th>
						</tr>
					</thead>
					<tbody>
						{customTargets.map((target, index) => (
							<StreamTargetRow
								key={target.id || index}
								target={target}
								isStreamActive={isConnected}
								onEdit={() => onDoubleClick({ rowId: target.id })}
								onDelete={() => onDeleteRecord({
									streamTargetId: target.id,
									outputId: target.outputId,
									idRecord: target.idRecord,
								})}
								onToggle={() => onTurnStatus({
									stream_target_id: target.id,
									new_status: target.enable !== false ? 'disable' : 'enable'
								})}
							/>
						))}
					</tbody>
				</table>
			)}

			{/* Help Text */}
			<div className="rs-help">
				<i className="fas fa-info-circle" />
				<div>
					<strong>Verificación de transmisión</strong>
					<p>
						Para confirmar que la transmisión está llegando correctamente a cada destino,
						verifica directamente en la plataforma correspondiente:
					</p>
					<ul style={{marginTop: '8px', paddingLeft: '20px'}}>
						<li><strong>YouTube:</strong> YouTube Studio → En vivo</li>
						<li><strong>Facebook:</strong> Facebook Live Producer</li>
						<li><strong>Twitch:</strong> Twitch Dashboard → Stream Manager</li>
					</ul>
					<p style={{marginTop: '10px'}}>
						Si la transmisión no aparece o muestra error, verifica la clave de stream del destino.
					</p>
				</div>
			</div>
		</ModulePanel>
	);
};

export default View;

// Stream Status Component
const StreamStatus = ({ isConnected }) => (
	<div className={`rs-stream-status ${isConnected ? 'active' : 'inactive'}`}>
		<span className="rs-status-dot"></span>
		<span>{isConnected ? 'Stream en vivo' : 'Stream detenido'}</span>
	</div>
);

// Stream Target Row Component
const StreamTargetRow = ({ target, isStreamActive, onEdit, onDelete, onToggle }) => {
	const platform = getPlatform(target.type, target.server);
	const isEnabled = target.enable !== false;

	return (
		<tr className={!isEnabled ? 'rs-row-disabled' : ''}>
			{/* Platform Icon */}
			<td>
				<div className="rs-platform-icon" style={{ backgroundColor: platform.color }}>
					<i className={platform.icon} />
				</div>
			</td>

			{/* Target Info */}
			<td>
				<div className="rs-target-info">
					<strong>{target.name || 'Sin nombre'}</strong>
					<span>{platform.name} • {target.server ? truncate(target.server, 40) : 'Sin servidor'}</span>
				</div>
			</td>

			{/* Toggle */}
			<td>
				<label className="rs-toggle">
					<input
						type="checkbox"
						checked={isEnabled}
						onChange={onToggle}
					/>
					<span className="rs-toggle-slider"></span>
				</label>
			</td>

			{/* Actions */}
			<td>
				<div className="rs-actions">
					<button className="rs-action-btn" onClick={onEdit} title="Editar">
						<i className="fas fa-pen" />
					</button>
					<button className="rs-action-btn rs-action-delete" onClick={onDelete} title="Eliminar">
						<i className="fas fa-trash" />
					</button>
				</div>
			</td>
		</tr>
	);
};

// Get platform info
const getPlatform = (type, server) => {
	if (type === 'youtube' || server?.includes('youtube.com')) {
		return { name: 'YouTube', icon: 'fab fa-youtube', color: '#ff0000' };
	}
	if (type === 'facebook' || server?.includes('facebook.com')) {
		return { name: 'Facebook', icon: 'fab fa-facebook-f', color: '#1877f2' };
	}
	if (type === 'twitch' || server?.includes('twitch.tv')) {
		return { name: 'Twitch', icon: 'fab fa-twitch', color: '#9146ff' };
	}
	if (server?.includes('instagram.com')) {
		return { name: 'Instagram', icon: 'fab fa-instagram', color: '#e4405f' };
	}
	return { name: 'RTMP', icon: 'fas fa-broadcast-tower', color: '#6c757d' };
};

// Truncate string
const truncate = (str, len) => str?.length > len ? str.substring(0, len) + '...' : str;
